import { DocumentFilter, TickCircle } from "iconsax-reactjs"
import { parseAsBoolean, useQueryState } from "nuqs"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"

import { BottomSheetModal } from "@/components/shared"
import { Button } from "@/components/ui"
import {
  DEFAULT_FILTER_EXPLORE,
  type FilterExploreValues,
  useFilterExplore
} from "@/modules/helpers/hooks/shared/use-filter-explore"
import { cn } from "@/modules/helpers/hooks/shared/utils"

const ORDER_OPTIONS = [
  { key: "DESC" as const, label: "Mới nhất" },
  { key: "ASC" as const, label: "Cũ nhất" }
]

const SELF_FACE_OPTIONS = [
  { key: "all", label: "Tất cả chiến dịch", value: null, disabled: false },
  { key: "show_face", label: "Show mặt", value: true, disabled: false },
  { key: "z_xu", label: "Z Xu", value: false, disabled: true }
] as const

const COMMISSION_RATE_OPTIONS = [
  // { key: "NONE", label: "Không lọc" },
  { key: "MORE_THAN_15", label: "Nhiều hơn 15%" },
  { key: "11_TO_14", label: "11 - 14%" },
  { key: "6_TO_10", label: "6 - 10%" },
  { key: "3_TO_5", label: "3 - 5%" }
] as const

type FilterFormValues = FilterExploreValues

const createDefaultFormValues = (): FilterFormValues => ({
  ...DEFAULT_FILTER_EXPLORE
})

export function FilterExplore() {
  const [isVisible, setIsVisible] = useQueryState(
    "filterExplore",
    parseAsBoolean.withDefault(false)
  )
  const form = useForm<FilterFormValues>({
    defaultValues: createDefaultFormValues()
  })

  const { filter, setFilter, reset: resetFilterState, calculateFilterCount } = useFilterExplore()

  useEffect(() => {
    form.reset(filter ?? createDefaultFormValues())
  }, [filter, form])

  const filterCount = calculateFilterCount()
  const handleApplyFilters = () => {
    form.handleSubmit((values) => {
      setFilter({ ...values })
      setIsVisible(false)
    })()
  }

  const handleResetFilters = () => {
    resetFilterState()
    form.reset(createDefaultFormValues())
  }

  return (
    <>
      <div
        className="rounded-xl bg-white w-12 h-12 border flex justify-center items-center relative"
        data-tour="filter-explore"
        onClick={() => setIsVisible(true)}
      >
        {filterCount > 0 && (
          <span className="bg-red-500 text-white flex justify-center items-center text-[10px] font-medium w-4 h-4 rounded-full absolute -top-1 -right-1">
            {filterCount}
          </span>
        )}
        <DocumentFilter size={24} color="#045664" />
      </div>

      <BottomSheetModal isVisible={isVisible} setIsVisible={setIsVisible} title="Bộ lọc">
        <div className="flex flex-col gap-6 p-2 px-4">
          {/* Separator line */}
          <div className="h-px bg-gray-200 -mt-4 mb-2" />

          {/* Sắp xếp theo */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Sắp xếp theo</label>
            <Controller
              name="order"
              control={form.control}
              render={({ field }) => (
                <div className="flex gap-2">
                  {ORDER_OPTIONS.map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "text-xs rounded-lg flex items-center gap-2 px-3 py-2.5 h-auto bg-white border-gray-300 text-gray-800",
                          {}
                        )}
                        onClick={() => field.onChange(item.key)}
                      >
                        {isSelected ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center">
                            <TickCircle size={12} className="text-secondary-100" variant="Bold" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-lg border border-gray-300" />
                        )}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Chiến dịch */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Chiến dịch</label>
            <Controller
              name="isSelfFaceRequired"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {SELF_FACE_OPTIONS.map((item) => {
                    const isSelected = field.value === item.value
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        disabled={item.disabled}
                        size="sm"
                        className={cn("text-xs rounded-lg px-4 py-2.5 h-auto", {
                          "bg-gradient-primary border-0 text-white": isSelected,
                          "bg-white border-gray-300 text-gray-800": !isSelected
                        })}
                        onClick={() => field.onChange(item.value)}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Tỷ lệ hoa hồng */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Tỷ lệ hoa hồng</label>
            <Controller
              name="commissionRange"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {COMMISSION_RATE_OPTIONS.map((item) => {
                      const isSelected = field.value === item.key
                      return (
                        <Button
                          key={item.key}
                          variant="outline"
                          size="sm"
                          className={cn("text-xs rounded-lg px-4 py-2.5 h-auto", {
                            "bg-gradient-primary border-0 text-white": isSelected,
                            "bg-white border-gray-300 text-gray-800": !isSelected
                          })}
                          onClick={() => {
                            // toggle
                            if (isSelected) {
                              field.onChange(null)
                            } else {
                              field.onChange(item.key)
                            }
                          }}
                        >
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <Button
              variant="outline"
              className="w-28 text-sm h-12 border-gray-200 bg-white text-primary-800 rounded-xl"
              onClick={handleResetFilters}
            >
              Đặt lại
            </Button>
            <Button
              className="flex-1 text-sm h-12 rounded-xl bg-primary font-medium"
              onClick={handleApplyFilters}
            >
              Xem kết quả
            </Button>
          </div>
        </div>
      </BottomSheetModal>
    </>
  )
}
